/**
 * PricingRuleHeaderBadge — the media tile left of the title in the detail header
 * (StandardDetailPage's `headerMedia` slot, same place the Customer page puts the
 * avatar). Shows the reward at a glance: "10% / OFF", "฿100 / OFF", "FREE / ITEM".
 */

import { Ticket, Tag } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";
import { rewardBadge, isCouponRule } from "./pricingRuleDetailModel";

export function PricingRuleHeaderBadge({ formData }: { formData: Record<string, any> }) {
  const { language: lang } = useLanguage();
  const { value, unit } = rewardBadge(formData, lang);
  const coupon = isCouponRule(formData);

  return (
    <div className="relative shrink-0">
      <div
        className={cn(
          "flex h-16 w-16 flex-col items-center justify-center rounded-2xl px-1 text-center",
          "bg-gradient-to-br from-sky-500 to-sky-600 text-white",
          "shadow-[0_2px_8px_rgba(2,132,199,0.35)] ring-2 ring-white dark:ring-neutral-900",
        )}
      >
        <span className="text-[15px] font-bold leading-none tracking-tight break-all">{value}</span>
        {unit && <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider opacity-90">{unit}</span>}
      </div>
      {/* Corner glyph: coupon-triggered vs automatic rule */}
      <span
        className="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-sky-600 shadow-[0_1px_4px_rgba(0,0,0,0.2)] ring-1 ring-black/5 dark:bg-neutral-900 dark:text-sky-400"
        title={coupon ? "Coupon-triggered" : "Automatic rule"}
      >
        {coupon ? <Ticket className="h-3.5 w-3.5" /> : <Tag className="h-3.5 w-3.5" />}
      </span>
    </div>
  );
}
